import { useState, useReducer, useRef, useEffect, useCallback, type FC, type ReactNode, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import * as floorPlanApi from "../lib/api/floorPlan.apis";
import Spinner from "./Components/Spinner";
import { SectionModal, type SectionFormData } from "./TableManagementTab";
import * as tablesApi from "../lib/api/tables.apis";

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════
type ElemType = "table"|"wall"|"door"|"window"|"bar"|"pool"|"tree"|"umbrella"|"stage"|"divider";
type TShape   = "circle"|"square"|"rectangle";
type TStatus  = "available"|"reserved"|"seated"|"occupied"|"inactive";

interface Pos   { x: number; y: number }
interface Sz    { width: number; height: number }

interface FloorElement {
  id: string; type: ElemType; sectionId: string;
  position: Pos; size: Sz; rotation: number;
  locked?: boolean; zIndex: number;
  props: {
    seats?: number; shape?: TShape; name?: string;
    status?: TStatus; notes?: string; active?: boolean; color?: string;
  };
}

interface Section { id: string; name: string; color: string };
interface FPState  { elements: FloorElement[]; sections: Section[]; name: string }

type Act =
  | { t:"ADD";    el: FloorElement }
  | { t:"UPD";    id: string; up: Partial<FloorElement> }
  | { t:"UPDP";   id: string; pp: Partial<FloorElement["props"]> }
  | { t:"DEL";    id: string }
  | { t:"DELM";   ids: string[] }
  | { t:"DUP";    id: string }
  | { t:"ADDSEC"; s: Section }
  | { t:"UPDSEC"; id: string; up: Partial<Section> }
  | { t:"DELSEC"; id: string }
  | { t:"CLEAR" }
  | { t:"LOAD";   s: FPState };

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const GS = 20;

const ST: Record<TStatus, { bg:string; bd:string; tx:string; dot:string; lbl:string }> = {
  available: { bg:"#dcfce7", bd:"#22c55e", tx:"#15803d", dot:"#22c55e", lbl:"Available" },
  reserved:  { bg:"#dbeafe", bd:"#3b82f6", tx:"#1d4ed8", dot:"#3b82f6", lbl:"Reserved"  },
  seated:    { bg:"#ffedd5", bd:"#f97316", tx:"#c2410c", dot:"#f97316", lbl:"Seated"    },
  occupied:  { bg:"#fee2e2", bd:"#ef4444", tx:"#b91c1c", dot:"#ef4444", lbl:"Occupied"  },
  inactive:  { bg:"#f1f5f9", bd:"#94a3b8", tx:"#64748b", dot:"#94a3b8", lbl:"Inactive"  },
};

const SEC_COLS = ["#f97316","#3b82f6","#8b5cf6","#22c55e","#ec4899","#14b8a6","#f59e0b","#6366f1"];

const STRUCTS: { type:ElemType; lbl:string; ico:string; dw:number; dh:number }[] = [
  { type:"wall",     lbl:"Wall",     ico:"▬",  dw:140, dh:16  },
  { type:"door",     lbl:"Door",     ico:"🚪", dw:60,  dh:18  },
  { type:"window",   lbl:"Window",   ico:"🪟", dw:80,  dh:16  },
  { type:"bar",      lbl:"Bar",      ico:"🍺", dw:140, dh:60  },
  { type:"pool",     lbl:"Pool",     ico:"💧", dw:120, dh:90  },
  { type:"tree",     lbl:"Tree",     ico:"🌳", dw:52,  dh:52  },
  { type:"umbrella", lbl:"Umbrella", ico:"☂️", dw:64,  dh:64  },
  { type:"stage",    lbl:"Stage",    ico:"🎭", dw:160, dh:90  },
  { type:"divider",  lbl:"Divider",  ico:"━",  dw:100, dh:10  },
];

const STRUCT_CLR: Record<string,string> = {
  wall:"#475569", door:"#92400e", window:"#0369a1",
  bar:"#78350f",  pool:"#0891b2", tree:"#166534",
  umbrella:"#b45309", stage:"#7c3aed", divider:"#6b7280",
};

const TPRESETS = [
  { seats:2, lbl:"2-Seater", shape:"circle"    as TShape, dw:64,  dh:64  },
  { seats:4, lbl:"4-Seater", shape:"square"    as TShape, dw:84,  dh:84  },
  { seats:6, lbl:"6-Seater", shape:"rectangle" as TShape, dw:124, dh:72  },
  { seats:8, lbl:"8-Seater", shape:"rectangle" as TShape, dw:164, dh:82  },
  { seats:0, lbl:"Custom",   shape:"square"    as TShape, dw:84,  dh:84  },
];

// ═══════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════
const uid  = () => Math.random().toString(36).slice(2,9);
const snap = (v:number) => Math.round(v/GS)*GS;
const clamp = (v:number,a:number,b:number) => Math.max(a,Math.min(b,v));

function seatPts(shape:TShape, w:number, h:number, n:number): Pos[] {
  if (!n) return [];
  const pts:Pos[] = [];
  if (shape==="circle") {
    const r = Math.min(w,h)/2 + 9;
    for (let i=0;i<n;i++) {
      const a = (i/n)*2*Math.PI - Math.PI/2;
      pts.push({ x:w/2+r*Math.cos(a), y:h/2+r*Math.sin(a) });
    }
  } else {
    const per = 2*(w+h);
    const step = per/n;
    for (let i=0;i<n;i++) {
      const d = i*step + step/2;
      if      (d<=w)       pts.push({x:d,       y:-10});
      else if (d<=w+h)     pts.push({x:w+10,    y:d-w});
      else if (d<=2*w+h)   pts.push({x:w-(d-w-h), y:h+10});
      else                 pts.push({x:-10,      y:h-(d-2*w-h)});
    }
  }
  return pts;
}

// ═══════════════════════════════════════════════════════
// REDUCER
// ═══════════════════════════════════════════════════════
const INIT_STATE: FPState = {
  name: "My Restaurant",
  sections: [],
  elements: [],
};

const isObjectId = (value: string) => /^[a-f\d]{24}$/i.test(value);

const tablePresetForCapacity = (capacity: number): { shape: TShape; width: number; height: number } => {
  if (capacity <= 2) return { shape: "circle", width: 64, height: 64 };
  if (capacity <= 4) return { shape: "square", width: 84, height: 84 };
  if (capacity <= 6) return { shape: "rectangle", width: 124, height: 72 };
  if (capacity <= 8) return { shape: "rectangle", width: 164, height: 82 };
  return { shape: "rectangle", width: 180, height: 90 };
};

const normalizeFloorPlan = (data?: floorPlanApi.FloorPlanResponse | null): FPState => {
  if (!data) return INIT_STATE;

  const sections: Section[] = (data.sections || []).map((section, idx) => ({
    id: section._id,
    name: section.name,
    color: section.color || SEC_COLS[idx % SEC_COLS.length],
  }));

  const sectionIds = new Set(sections.map((section) => section.id));

  const colCount = 6;
  const cellW = 140;
  const cellH = 120;
  const offsetX = 80;
  const offsetY = 90;

  const elements: FloorElement[] = (data.tables || [])
    .filter((table) => sectionIds.has(table.sectionId))
    .map((table, idx) => {
      const cap = Number(table.capacity) || 4;
      const preset = tablePresetForCapacity(cap);
      const layout = table.layout || {};
      const row = Math.floor(idx / colCount);
      const col = idx % colCount;

      const position = layout.position || { x: offsetX + col * cellW, y: offsetY + row * cellH };
      const size = layout.size || { width: preset.width, height: preset.height };
      const shape = (layout.shape as TShape) || preset.shape;

      return {
        id: table._id,
        type: "table",
        sectionId: table.sectionId,
        position,
        size,
        rotation: layout.rotation ?? 0,
        locked: layout.locked ?? false,
        zIndex: layout.zIndex ?? idx,
        props: {
          seats: cap,
          shape,
          name: table.name,
          status: "available",
          notes: table.notes || "",
          active: table.isActive ?? true,
        },
      };
    });

  return {
    name: INIT_STATE.name,
    sections,
    elements,
  };
};

function reducer(s: FPState, a: Act): FPState {
  switch (a.t) {
    case "ADD":    return {...s, elements:[...s.elements, a.el]};
    case "UPD":    return {...s, elements:s.elements.map(e=>e.id===a.id?{...e,...a.up}:e)};
    case "UPDP":   return {...s, elements:s.elements.map(e=>e.id===a.id?{...e,props:{...e.props,...a.pp}}:e)};
    case "DEL":    return {...s, elements:s.elements.filter(e=>e.id!==a.id)};
    case "DELM":   return {...s, elements:s.elements.filter(e=>!a.ids.includes(e.id))};
    case "DUP": {
      const o=s.elements.find(e=>e.id===a.id); if(!o) return s;
      return {...s, elements:[...s.elements,{...o,id:uid(),position:{x:o.position.x+20,y:o.position.y+20},zIndex:Math.max(0,...s.elements.map(e=>e.zIndex))+1}]};
    }
    case "ADDSEC": return {...s, sections:[...s.sections,a.s]};
    case "UPDSEC": return {...s, sections:s.sections.map(sc=>sc.id===a.id?{...sc,...a.up}:sc)};
    case "DELSEC": return {...s, sections:s.sections.filter(sc=>sc.id!==a.id), elements:s.elements.filter(e=>e.sectionId!==a.id)};
    case "CLEAR":  return {...s, elements:[]};
    case "LOAD":   return a.s;
    default:       return s;
  }
}

// ═══════════════════════════════════════════════════════
// SVG ICONS
// ═══════════════════════════════════════════════════════
const I: FC<{p:string;s?:number;cls?:string}> = ({p,s=16,cls=""}) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cls}>
    <path d={p}/>
  </svg>
);
const IC = {
  menu:   "M4 6h16M4 12h16M4 18h16",
  plus:   "M12 5v14M5 12h14",
  trash:  "M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6",
  edit:   "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4z",
  save:   "M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2zM17 21v-8H7v8M7 3v5h8",
  down:   "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3",
  undo:   "M3 7v6h6M3.51 15A9 9 0 1021 12",
  redo:   "M21 7v6h-6M20.49 15A9 9 0 113 12",
  grid:   "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  zplus:  "M11 8v6M8 11h6M21 21l-4.35-4.35A7 7 0 118.7 8.7",
  zminus: "M8 11h6M21 21l-4.35-4.35A7 7 0 118.7 8.7",
  lock:   "M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4",
  unlock: "M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 019.9-1",
  x:      "M18 6L6 18M6 6l12 12",
  check:  "M20 6L9 17l-5-5",
  copy:   "M20 9h-9a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-9a2 2 0 00-2-2zM5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1",
  chev_d: "M6 9l6 6 6-6",
  layers: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  rotate: "M23 4v6h-6M20.49 15a9 9 0 11-2.12-9.36L23 10",
  prop:   "M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z",
  eye:    "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z",
};

// ═══════════════════════════════════════════════════════
// SMALL HELPERS
// ═══════════════════════════════════════════════════════
const Label: FC<{c:ReactNode}> = ({c}) => (
  <p style={{margin:"0 0 5px",fontSize:10,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.07em"}}>{c}</p>
);

const Inp: FC<{value:string|number;onChange:(v:string)=>void;type?:string;min?:number;max?:number;placeholder?:string;style?:CSSProperties}> = ({value,onChange,type="text",min,max,placeholder,style={}}) => (
  <input type={type} value={value} onChange={e=>onChange(e.target.value)} min={min} max={max} placeholder={placeholder}
    style={{width:"100%",padding:"7px 10px",border:"1.5px solid #e2e8f0",borderRadius:8,fontSize:13,color:"#0f172a",background:"#fff",outline:"none",boxSizing:"border-box",...style}}/>
);

const Sel: FC<{value:string;onChange:(v:string)=>void;children:ReactNode;style?:CSSProperties}> = ({value,onChange,children,style={}}) => (
  <select value={value} onChange={e=>onChange(e.target.value)}
    style={{width:"100%",padding:"7px 10px",border:"1.5px solid #e2e8f0",borderRadius:8,fontSize:13,color:"#0f172a",background:"#fff",outline:"none",boxSizing:"border-box",...style}}>
    {children}
  </select>
);

const Toggle: FC<{on:boolean;onToggle:()=>void;label:string}> = ({on,onToggle,label}) => (
  <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
    <div onClick={onToggle} style={{width:40,height:22,borderRadius:11,background:on?"#f97316":"#e2e8f0",position:"relative",cursor:"pointer",transition:"background .2s",flexShrink:0}}>
      <div style={{position:"absolute",top:2,left:on?20:2,width:18,height:18,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 4px rgba(0,0,0,.2)",transition:"left .2s"}}/>
    </div>
    <span style={{fontSize:12,fontWeight:600,color:"#374151"}}>{label}</span>
  </label>
);

// ═══════════════════════════════════════════════════════
// TABLE VISUAL ON CANVAS
// ═══════════════════════════════════════════════════════
const TableEl: FC<{
  el: FloorElement; sel: boolean; hov: boolean;
  onMD: (e:React.MouseEvent)=>void;
}> = ({el,sel,hov,onMD}) => {
  const {seats=4,shape="square",name="T",status="available",active=true} = el.props;
  const c = ST[active ? status : "inactive"];
  const {width:w,height:h} = el.size;
  const pts = seatPts(shape,w,h,seats);
  const isC = shape==="circle";
  const br  = isC?"50%":shape==="square"?"12px":"10px";

  return (
    <div onMouseDown={onMD} style={{position:"absolute",left:el.position.x,top:el.position.y,width:w,height:h,
      transform:`rotate(${el.rotation}deg)`,transformOrigin:"center",zIndex:el.zIndex,
      cursor:el.locked?"default":"move",userSelect:"none"}}>
      {/* Seat dots */}
      {pts.map((pt,i)=>(
        <div key={i} style={{position:"absolute",left:pt.x-5.5,top:pt.y-5.5,width:11,height:11,
          borderRadius:"50%",background:c.bd,border:"2px solid #fff",
          boxShadow:"0 1px 4px rgba(0,0,0,.25)",zIndex:1,pointerEvents:"none"}}/>
      ))}
      {/* Table body */}
      <div style={{position:"absolute",inset:0,borderRadius:br,background:c.bg,
        border:`2.5px solid ${sel?"#f97316":c.bd}`,
        boxShadow:sel?"0 0 0 3px rgba(249,115,22,.22),0 4px 18px rgba(0,0,0,.14)"
                   :hov?"0 4px 14px rgba(0,0,0,.1)":"0 2px 8px rgba(0,0,0,.07)",
        display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:1,
        transition:"box-shadow .15s"}}>
        <span style={{fontSize:Math.min(13,w*.16),fontWeight:800,color:c.tx,lineHeight:1,textAlign:"center",wordBreak:"break-all",padding:"0 4px"}}>{name}</span>
        <span style={{fontSize:Math.min(10,w*.13),color:c.tx,opacity:.7,lineHeight:1}}>{seats}p</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// STRUCTURAL VISUAL ON CANVAS
// ═══════════════════════════════════════════════════════
const StructEl: FC<{
  el: FloorElement; sel: boolean; hov: boolean;
  onMD: (e:React.MouseEvent)=>void;
}> = ({el,sel,hov,onMD}) => {
  const color = STRUCT_CLR[el.type]||"#64748b";
  const item  = STRUCTS.find(s=>s.type===el.type);
  const {width:w,height:h} = el.size;
  const thin = h<=20;

  return (
    <div onMouseDown={onMD} style={{position:"absolute",left:el.position.x,top:el.position.y,width:w,height:h,
      transform:`rotate(${el.rotation}deg)`,transformOrigin:"center",zIndex:el.zIndex,
      cursor:el.locked?"default":"move",userSelect:"none"}}>
      <div style={{position:"absolute",inset:0,borderRadius:thin?3:10,
        background:thin?color:color+"20",
        border:`2px solid ${sel?"#f97316":hov?color:color+"70"}`,
        boxShadow:sel?"0 0 0 3px rgba(249,115,22,.22)":hov?"0 4px 12px rgba(0,0,0,.1)":"none",
        display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",
        transition:"box-shadow .15s"}}>
        {!thin && (
          <div style={{textAlign:"center",pointerEvents:"none"}}>
            <div style={{fontSize:Math.min(26,Math.min(w,h)*.5)}}>{item?.ico||"?"}</div>
            {Math.min(w,h)>52 && <div style={{fontSize:10,color,fontWeight:700,marginTop:2}}>{item?.lbl}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// RESIZE / ROTATE HANDLES
// ═══════════════════════════════════════════════════════
type HDir = "nw"|"n"|"ne"|"e"|"se"|"s"|"sw"|"w";
const HDIRS: HDir[] = ["nw","n","ne","e","se","s","sw","w"];
const HCURSOR: Record<HDir,string> = {nw:"nw-resize",n:"n-resize",ne:"ne-resize",e:"e-resize",se:"se-resize",s:"s-resize",sw:"sw-resize",w:"w-resize"};

function hPos(d:HDir,w:number,h:number):{l:number;t:number} {
  const m = {nw:{l:0,t:0},n:{l:w/2,t:0},ne:{l:w,t:0},e:{l:w,t:h/2},
             se:{l:w,t:h},s:{l:w/2,t:h},sw:{l:0,t:h},w:{l:0,t:h/2}};
  return m[d];
}

const Handles: FC<{
  el:FloorElement;
  onResize:(h:HDir,e:React.MouseEvent)=>void;
  onRotate:(e:React.MouseEvent)=>void;
}> = ({el,onResize,onRotate}) => {
  const {width:w,height:h} = el.size;
  return (
    <div style={{position:"absolute",left:el.position.x,top:el.position.y,width:w,height:h,
      transform:`rotate(${el.rotation}deg)`,transformOrigin:"center",
      zIndex:el.zIndex+9000,pointerEvents:"none"}}>
      {/* Dashed border */}
      <div style={{position:"absolute",inset:-2,border:"2px dashed rgba(249,115,22,.5)",borderRadius:4,pointerEvents:"none"}}/>
      {/* Rotate handle */}
      <div onMouseDown={e=>{e.stopPropagation();onRotate(e);}}
        style={{position:"absolute",left:w/2-8,top:-26,width:16,height:16,borderRadius:"50%",
          background:"#f97316",border:"2.5px solid #fff",cursor:"crosshair",pointerEvents:"all",
          boxShadow:"0 2px 8px rgba(249,115,22,.5)"}}>
        <div style={{position:"absolute",left:6,top:14,width:2,height:10,background:"rgba(249,115,22,.6)"}}/>
      </div>
      {/* Resize handles */}
      {HDIRS.map(d=>{
        const p=hPos(d,w,h);
        return (
          <div key={d} onMouseDown={e=>{e.stopPropagation();onResize(d,e);}}
            style={{position:"absolute",left:p.l-5,top:p.t-5,width:10,height:10,
              borderRadius:2,background:"#fff",border:"2px solid #f97316",
              cursor:HCURSOR[d],pointerEvents:"all",boxShadow:"0 1px 5px rgba(0,0,0,.2)"}}/>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// TABLE MODAL
// ═══════════════════════════════════════════════════════
interface TModalData { name:string;seats:number;shape:TShape;sectionId:string;status:TStatus;notes:string;active:boolean }

const TableModal: FC<{
  init?: Partial<TModalData>;
  sections: Section[];
  mode:"create"|"edit";
  onSave:(d:TModalData)=>void;
  onClose:()=>void;
}> = ({init,sections,mode,onSave,onClose}) => {
  const [f,setF] = useState<TModalData>({
    name:   init?.name  || `T${String(Math.floor(Math.random()*90)+10)}`,
    seats:  init?.seats || 4,
    shape:  init?.shape || "square",
    sectionId: init?.sectionId || sections[0]?.id||"",
    status: init?.status|| "available",
    notes:  init?.notes || "",
    active: init?.active??true,
  });
  const up = (k:keyof TModalData,v:any)=>setF(p=>({...p,[k]:v}));

  const c = ST[f.status];
  const pw = f.shape==="rectangle"?120:80;
  const ph = f.shape==="rectangle"?64:80;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,.6)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(5px)"}}>
      <div style={{background:"#fff",borderRadius:20,width:"min(660px,95vw)",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 30px 80px rgba(0,0,0,.28)"}}>
        {/* Header */}
        <div style={{padding:"20px 24px 16px",borderBottom:"1px solid #f1f5f9",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",borderRadius:"20px 20px 0 0",zIndex:10}}>
          <div>
            <h2 style={{margin:0,fontSize:18,fontWeight:800,color:"#0f172a"}}>{mode==="create"?"New Table":"Edit Table"}</h2>
            <p style={{margin:"2px 0 0",fontSize:12,color:"#94a3b8"}}>Configure table properties and placement</p>
          </div>
          <button onClick={onClose} style={{background:"#f1f5f9",border:"none",cursor:"pointer",borderRadius:10,padding:8,display:"flex",color:"#64748b"}}>
            <I p={IC.x} s={16}/>
          </button>
        </div>
        <div style={{padding:"20px 24px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 220px",gap:24}}>
            {/* Left */}
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <Label c="Table Name *"/>
                  <Inp value={f.name} onChange={v=>up("name",v)} placeholder="e.g. T1, VIP-3"/>
                </div>
                <div>
                  <Label c="Seats *"/>
                  <Inp type="number" value={f.seats} onChange={v=>up("seats",Math.max(1,Math.min(20,+v)))} min={1} max={20}/>
                </div>
              </div>

              {/* Shape */}
              <div>
                <Label c="Shape"/>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                  {(["circle","square","rectangle"] as TShape[]).map(sh=>(
                    <button key={sh} onClick={()=>up("shape",sh)} style={{
                      padding:"10px 6px",borderRadius:12,
                      border:`2px solid ${f.shape===sh?"#f97316":"#e2e8f0"}`,
                      background:f.shape===sh?"#fff7ed":"#f8fafc",
                      cursor:"pointer",transition:"all .15s",
                    }}>
                      <div style={{
                        margin:"0 auto 6px",
                        width:sh==="rectangle"?38:22,height:sh==="rectangle"?16:22,
                        borderRadius:sh==="circle"?"50%":sh==="rectangle"?"4px":"6px",
                        background:f.shape===sh?"#f97316":"#94a3b8",transition:"all .15s",
                      }}/>
                      <div style={{fontSize:11,fontWeight:700,color:f.shape===sh?"#c2410c":"#64748b",textTransform:"capitalize"}}>{sh}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Section */}
              <div>
                <Label c="Section"/>
                <Sel value={f.sectionId} onChange={v=>up("sectionId",v)}>
                  {sections.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </Sel>
              </div>

              {/* Status */}
              <div>
                <Label c="Status"/>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {(Object.keys(ST) as TStatus[]).map(st=>{
                    const sc=ST[st];
                    return (
                      <button key={st} onClick={()=>up("status",st)} style={{
                        display:"flex",alignItems:"center",gap:6,
                        padding:"5px 12px",borderRadius:20,cursor:"pointer",
                        border:`1.5px solid ${f.status===st?sc.bd:"#e2e8f0"}`,
                        background:f.status===st?sc.bg:"#f8fafc",
                        color:f.status===st?sc.tx:"#94a3b8",
                        fontSize:11,fontWeight:700,transition:"all .15s",
                      }}>
                        <div style={{width:6,height:6,borderRadius:"50%",background:f.status===st?sc.dot:"#cbd5e1"}}/>
                        {sc.lbl}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label c="Notes (optional)"/>
                <textarea value={f.notes} onChange={e=>up("notes",e.target.value)} rows={2}
                  placeholder="e.g. Near window, corner booth"
                  style={{width:"100%",padding:"8px 10px",border:"1.5px solid #e2e8f0",borderRadius:8,fontSize:13,color:"#0f172a",background:"#fff",outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
              </div>

              <Toggle on={f.active} onToggle={()=>up("active",!f.active)} label="Table is active"/>
            </div>

            {/* Right: preview */}
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:"#f8fafc",borderRadius:14,padding:16,flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:180}}>
                <Label c="Preview"/>
                <div style={{position:"relative",width:pw+32,height:ph+32,display:"flex",alignItems:"center",justifyContent:"center",marginTop:8}}>
                  {seatPts(f.shape,pw,ph,f.seats).map((pt,i)=>(
                    <div key={i} style={{position:"absolute",left:16+pt.x-5.5,top:16+pt.y-5.5,width:11,height:11,
                      borderRadius:"50%",background:c.bd,border:"2px solid #fff",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                  ))}
                  <div style={{width:pw,height:ph,borderRadius:f.shape==="circle"?"50%":f.shape==="square"?"12px":"10px",
                    background:c.bg,border:`2.5px solid ${c.bd}`,
                    display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:2,
                    boxShadow:"0 4px 14px rgba(0,0,0,.1)"}}>
                    <span style={{fontSize:14,fontWeight:800,color:c.tx}}>{f.name||"T?"}</span>
                    <span style={{fontSize:11,color:c.tx,opacity:.7}}>{f.seats}p</span>
                  </div>
                </div>
              </div>
              {/* Status legend */}
              <div style={{background:"#f8fafc",borderRadius:12,padding:"10px 12px"}}>
                <Label c="Current Status"/>
                <div style={{display:"flex",alignItems:"center",gap:7,marginTop:4}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:c.dot}}/>
                  <span style={{fontSize:13,fontWeight:700,color:c.tx}}>{ST[f.status].lbl}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:10,marginTop:20,paddingTop:16,borderTop:"1px solid #f1f5f9"}}>
            <button onClick={onClose} style={{padding:"10px 20px",background:"#f1f5f9",color:"#374151",border:"none",borderRadius:12,cursor:"pointer",fontSize:13,fontWeight:700}}>
              Cancel
            </button>
            <button onClick={()=>onSave(f)} style={{
              padding:"10px 28px",background:"#f97316",color:"#fff",border:"none",borderRadius:12,
              cursor:"pointer",fontSize:13,fontWeight:800,
              boxShadow:"0 4px 14px rgba(249,115,22,.4)"}}>
              {mode==="create"?"Add to Floor":"Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// PROPERTIES PANEL
// ═══════════════════════════════════════════════════════
const PropsPanel: FC<{
  el: FloorElement|null;
  sections: Section[];
  dispatch: (a:Act)=>void;
  onEditTable:(el:FloorElement)=>void;
  onClose:()=>void;
}> = ({el,sections,dispatch,onEditTable,onClose}) => {
  if (!el) return (
    <div style={{width:256,borderLeft:"1px solid #f1f5f9",background:"#fafafa",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:24,flexShrink:0}}>
      <div style={{width:52,height:52,borderRadius:16,background:"#f1f5f9",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <I p={IC.layers} s={24}/>
      </div>
      <p style={{margin:0,fontSize:13,color:"#94a3b8",textAlign:"center",lineHeight:1.7}}>
        Click any element on the canvas to inspect and edit its properties
      </p>
    </div>
  );

  const sec = sections.find(s=>s.id===el.sectionId);
  const isT = el.type==="table";
  const upd = (up:Partial<FloorElement>)=>dispatch({t:"UPD",id:el.id,up});
  const upP = (pp:Partial<FloorElement["props"]>)=>dispatch({t:"UPDP",id:el.id,pp});
  const set  = (k:string,v:any)=>upd({[k]:v} as any);

  return (
    <div style={{width:256,borderLeft:"1px solid #f1f5f9",background:"#fff",overflowY:"auto",flexShrink:0,display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{padding:"14px 16px 12px",borderBottom:"1px solid #f1f5f9",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:5}}>
        <div>
          <p style={{margin:0,fontSize:10,fontWeight:800,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.07em"}}>{isT?"Table":"Structural"}</p>
          <p style={{margin:"2px 0 0",fontSize:15,fontWeight:800,color:"#0f172a"}}>{el.props.name||el.type}</p>
        </div>
        <button onClick={onClose} style={{background:"#f1f5f9",border:"none",cursor:"pointer",borderRadius:8,padding:6,display:"flex",color:"#64748b"}}>
          <I p={IC.x} s={14}/>
        </button>
      </div>

      <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:14,flex:1}}>
        {/* Table status */}
        {isT && (
          <div>
            <Label c="Status"/>
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              {(Object.keys(ST) as TStatus[]).map(st=>{
                const sc=ST[st]; const on=el.props.status===st;
                return (
                  <button key={st} onClick={()=>upP({status:st})} style={{
                    display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
                    borderRadius:9,border:`1.5px solid ${on?sc.bd:"transparent"}`,
                    background:on?sc.bg:"transparent",cursor:"pointer",transition:"all .15s",
                  }}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:sc.dot,flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:on?700:500,color:on?sc.tx:"#64748b",flex:1,textAlign:"left"}}>{sc.lbl}</span>
                    {on&&<I p={IC.check} s={12}/>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Section */}
        <div>
          <Label c="Section"/>
          <div style={{display:"flex",alignItems:"center",gap:7,padding:"7px 10px",background:"#f8fafc",borderRadius:8,border:"1px solid #e2e8f0",marginBottom:6}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:sec?.color||"#94a3b8",flexShrink:0}}/>
            <span style={{fontSize:13,fontWeight:600,color:"#374151"}}>{sec?.name||"None"}</span>
          </div>
          <Sel value={el.sectionId} onChange={v=>upd({sectionId:v})}>
            {sections.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </Sel>
        </div>

        {/* Position */}
        <div>
          <Label c="Position"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {(["x","y"] as const).map(k=>(
              <div key={k}>
                <p style={{margin:"0 0 3px",fontSize:10,color:"#94a3b8",fontWeight:700}}>{k.toUpperCase()}</p>
                <Inp type="number" value={Math.round(el.position[k])} onChange={v=>upd({position:{...el.position,[k]:+v}})}/>
              </div>
            ))}
          </div>
        </div>

        {/* Size */}
        <div>
          <Label c="Size"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {(["width","height"] as const).map(k=>(
              <div key={k}>
                <p style={{margin:"0 0 3px",fontSize:10,color:"#94a3b8",fontWeight:700}}>{k==="width"?"W":"H"}</p>
                <Inp type="number" value={Math.round(el.size[k])} onChange={v=>upd({size:{...el.size,[k]:Math.max(20,+v)}})} min={20}/>
              </div>
            ))}
          </div>
        </div>

        {/* Rotation */}
        <div>
          <Label c={`Rotation (${Math.round(el.rotation)}°)`}/>
          <input type="range" min={0} max={360} value={el.rotation} onChange={e=>upd({rotation:+e.target.value})}
            style={{width:"100%",accentColor:"#f97316"}}/>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
            {[0,90,180,270].map(d=>(
              <button key={d} onClick={()=>upd({rotation:d})} style={{
                fontSize:10,fontWeight:700,color:el.rotation===d?"#f97316":"#94a3b8",
                background:"transparent",border:"none",cursor:"pointer",padding:"2px 4px",
              }}>{d}°</button>
            ))}
          </div>
        </div>

        {/* Lock */}
        <Toggle on={!!el.locked} onToggle={()=>upd({locked:!el.locked})} label="Lock position"/>

        {/* Actions */}
        <div style={{display:"flex",flexDirection:"column",gap:6,paddingTop:12,borderTop:"1px solid #f1f5f9",marginTop:4}}>
          {isT && (
            <button onClick={()=>onEditTable(el)} style={{
              display:"flex",alignItems:"center",gap:8,padding:"9px 12px",
              background:"#fff7ed",border:"1.5px solid #fed7aa",borderRadius:10,
              cursor:"pointer",color:"#c2410c",fontSize:12,fontWeight:700}}>
              <I p={IC.edit} s={14}/>Edit Table Details
            </button>
          )}
          <button onClick={()=>dispatch({t:"DUP",id:el.id})} style={{
            display:"flex",alignItems:"center",gap:8,padding:"9px 12px",
            background:"#f8fafc",border:"1.5px solid #e2e8f0",borderRadius:10,
            cursor:"pointer",color:"#374151",fontSize:12,fontWeight:700}}>
            <I p={IC.copy} s={14}/>Duplicate
          </button>
          <button onClick={()=>dispatch({t:"DEL",id:el.id})} style={{
            display:"flex",alignItems:"center",gap:8,padding:"9px 12px",
            background:"#fef2f2",border:"1.5px solid #fecaca",borderRadius:10,
            cursor:"pointer",color:"#b91c1c",fontSize:12,fontWeight:700}}>
            <I p={IC.trash} s={14}/>Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// SIDEBAR
// ═══════════════════════════════════════════════════════
const Sidebar: FC<{
  restaurantId: string;
  state: FPState;
  dispatch: (a:Act)=>void;
  activeSec: string|null;
  setActiveSec: (id:string|null)=>void;
  onTableClick:(p:typeof TPRESETS[0])=>void;
  onTableDragStart:(p:typeof TPRESETS[0])=>void;
  onStructDragStart:(s:typeof STRUCTS[0])=>void;
}> = ({state,dispatch,activeSec,setActiveSec,onTableClick,onTableDragStart,onStructDragStart, restaurantId}) => {
  const [secOpen, setSO] = useState(true);
  const [tblOpen, setTO] = useState(true);
  const [strOpen, setST] = useState(true);
  const [secModal, setSM] = useState<{mode:"create"|"edit";sec?:Section}|null>(null);

    const handleSaveSection = async (data: SectionFormData, id?: string) => {
      if (!restaurantId) throw new Error("Missing restaurant id.");
  
      if (id) {
        await tablesApi.updateSection(restaurantId, id, data);
        dispatch({t:"UPDSEC",id,up:{name:data.name}});
      } else {
        const created = await tablesApi.createSection(restaurantId, data);
        dispatch({t:"ADDSEC",s:{id:created.id,name:created.name,color:SEC_COLS[state.sections.length%SEC_COLS.length]}});
      }
    };

  const Row: FC<{label:string;sub?:string;left:ReactNode;onClick?:()=>void;style?:CSSProperties}> = ({label,sub,left,onClick,style={}}) => (
    <div onClick={onClick} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 10px",borderRadius:10,cursor:"pointer",transition:"background .12s",...style}}>
      <div style={{flexShrink:0}}>{left}</div>
      <div style={{flex:1,minWidth:0}}>
        <p style={{margin:0,fontSize:12,fontWeight:700,color:"#374151",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</p>
        {sub&&<p style={{margin:0,fontSize:10,color:"#94a3b8"}}>{sub}</p>}
      </div>
    </div>
  );

  const Hdr: FC<{lbl:string;open:boolean;onToggle:()=>void}> = ({lbl,open,onToggle}) => (
    <button onClick={onToggle} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"none",border:"none",cursor:"pointer"}}>
      <span style={{fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.08em",color:"#94a3b8"}}>{lbl}</span>
      <div style={{transform:open?"rotate(0)":"rotate(-90deg)",transition:"transform .2s",color:"#cbd5e1"}}><I p={IC.chev_d} s={14}/></div>
    </button>
  );

  return (
    <div style={{width:216,borderRight:"1px solid #f1f5f9",background:"#fff",overflowY:"auto",flexShrink:0,display:"flex",flexDirection:"column"}}>
      {/* Sections */}
      <div style={{borderBottom:"1px solid #f1f5f9"}}>
        <Hdr lbl="Sections" open={secOpen} onToggle={()=>setSO(!secOpen)}/>
        {secOpen && (
          <div style={{paddingBottom:8}}>
            {state.sections.map(s=>(
              <div key={s.id} onClick={()=>setActiveSec(activeSec===s.id?null:s.id)}
                style={{display:"flex",alignItems:"center",gap:7,padding:"6px 10px 6px 14px",
                  cursor:"pointer",background:activeSec===s.id?"#fff7ed":"transparent",
                  borderLeft:`3px solid ${activeSec===s.id?s.color:"transparent"}`,
                  transition:"all .12s"}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:s.color,flexShrink:0}}/>
                  <span style={{flex:1,fontSize:12,fontWeight:activeSec===s.id?800:500,color:activeSec===s.id?"#c2410c":"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                
                <div style={{display:"flex",gap:1,opacity:0,transition:"opacity .1s"}}
                  onMouseEnter={e=>(e.currentTarget.style.opacity="1")}
                  onMouseLeave={e=>(e.currentTarget.style.opacity="0.5")}>
                  <button onClick={e=>{e.stopPropagation(); setSM({mode:"edit",sec:s});}}
                    style={{background:"none",border:"none",cursor:"pointer",padding:2,color:"#94a3b8",borderRadius:4,display:"flex"}}>
                    <I p={IC.edit} s={11}/>
                  </button>
                </div>
              </div>
            ))}
            <button onClick={()=>{
              const s:Section={id:uid(),name:`Section ${state.sections.length+1}`,color:SEC_COLS[state.sections.length%SEC_COLS.length]};
              setSM({mode:"create",sec:s});
            }} style={{display:"flex",alignItems:"center",gap:6,width:"100%",padding:"6px 14px",background:"none",border:"none",cursor:"pointer",color:"#f97316",fontSize:12,fontWeight:700}}>
              <I p={IC.plus} s={13}/>Add Section
            </button>

            {secModal?.mode === "create" && (
              <SectionModal mode="add" onClose={() => setSM(null)} onSave={handleSaveSection} />
            )}
            {secModal?.mode === "edit" && (
              <SectionModal mode="edit" initial={secModal.sec} onClose={() => setSM(null)} onSave={handleSaveSection} />
            )}
          </div>
        )}
      </div>

      {/* Tables */}
      <div style={{borderBottom:"1px solid #f1f5f9"}}>
        <Hdr lbl="Tables" open={tblOpen} onToggle={()=>setTO(!tblOpen)}/>
        {tblOpen && (
          <div style={{padding:"0 10px 10px",display:"flex",flexDirection:"column",gap:4}}>
            {TPRESETS.map(p=>(
              <div key={p.lbl} draggable onDragStart={()=>onTableDragStart(p)} onClick={()=>onTableClick(p)}
                style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"#f8fafc",border:"1.5px solid #e2e8f0",borderRadius:10,cursor:"grab",userSelect:"none",transition:"all .13s"}}>
                <div style={{
                  width:p.shape==="rectangle"?34:20,height:p.shape==="rectangle"?16:20,
                  borderRadius:p.shape==="circle"?"50%":p.shape==="rectangle"?"4px":"6px",
                  background:"#22c55e",border:"2px solid #16a34a",flexShrink:0,
                }}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontSize:12,fontWeight:800,color:"#374151"}}>{p.lbl}</p>
                  {p.seats>0&&<p style={{margin:0,fontSize:10,color:"#94a3b8"}}>{p.seats} seats</p>}
                </div>
                <I p="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" s={12}/>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Structural */}
      <div>
        <Hdr lbl="Structural" open={strOpen} onToggle={()=>setST(!strOpen)}/>
        {strOpen && (
          <div style={{padding:"0 10px 10px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {STRUCTS.map(s=>(
              <div key={s.type} draggable onDragStart={()=>onStructDragStart(s)}
                style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                  padding:"10px 6px",background:"#f8fafc",border:"1.5px solid #e2e8f0",
                  borderRadius:10,cursor:"grab",userSelect:"none",transition:"all .13s"}}>
                <span style={{fontSize:20}}>{s.ico}</span>
                <span style={{fontSize:10,fontWeight:700,color:"#64748b"}}>{s.lbl}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// API HELPERS
// ═══════════════════════════════════════════════════════
const toFloorPlanPayload = (s: FPState): Parameters<typeof floorPlanApi.putFloorPlan>[1] => ({
  sections: s.sections
    .filter((section) => isObjectId(section.id))
    .map((section) => ({ _id: section.id, name: section.name, color: section.color })),
  tables: s.elements
    .filter((el) => el.type === "table" && isObjectId(el.sectionId))
    .map((el) => ({
      ...(isObjectId(el.id) ? { _id: el.id } : {}),
      sectionId: el.sectionId,
      name: el.props.name || "Table",
      capacity: el.props.seats || 4,
      isActive: el.props.active ?? true,
      notes: el.props.notes || "",
      layout: {
        position: el.position,
        size: el.size,
        rotation: el.rotation,
        locked: el.locked ?? false,
        zIndex: el.zIndex,
        shape: (el.props.shape || "square") as any,
      },
    })),
});

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function FloorPlanBuilder() {
  const { restaurantId = "" } = useParams<{ restaurantId: string }>();
  const { accessToken } = useAuth();
  const [state, rawDispatch] = useReducer(reducer, INIT_STATE);
  const [initialLoad, setInitialLoad] = useState(false);

  const stateRef = useRef(state);
  stateRef.current = state;

  // Keep accessToken in a ref so token refreshes never re-trigger the load effect
  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  // Canvas refs & state
  const canvasRef  = useRef<HTMLDivElement>(null);
  const [zoom, setZoom]   = useState(0.5);
  const [pan,  setPan]    = useState<Pos>({x:40,y:40});
  const [grid,  setGrid]  = useState(true);
  const [snapOn,setSnap]  = useState(true);
  const [canvasLock,setCL]= useState(false);
  const [selId, setSelId] = useState<string|null>(null);
  const [selIds,setSelIds]= useState<string[]>([]);
  const [hovId, setHovId] = useState<string|null>(null);
  const [activeSec, setAS]= useState<string|null>(null);
  const [sbarOpen, setSBO]= useState(true);
  const [saveStatus, setSS]= useState<"saved"|"saving"|"unsaved">("saved");
  const [tModal, setTM]   = useState<{mode:"create"|"edit";el?:FloorElement;preset?:typeof TPRESETS[0];dropPos?:Pos}|null>(null);
  const [toast, setToast] = useState<{msg:string;ok:boolean}|null>(null);
  const [selBox,setSelBox]= useState<{s:Pos;e:Pos}|null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistenceEnabled = Boolean(restaurantId);

  // History tracking
  const [histStates, setHistStates] = useState<FPState[]>([INIT_STATE]);
  const [hIdx, setHIdx] = useState(0);
  const pushHist = useCallback((ns:FPState)=>{
    setHistStates(h=>[...h.slice(0,hIdx+1),ns]);
    setHIdx(i=>i+1);
  },[hIdx]);

  const showToast = useCallback((msg:string, ok=true) => {
    setToast({msg,ok});
    setTimeout(() => setToast(null), 2600);
  }, []);

  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  const syncNewTableIds = useCallback((response: floorPlanApi.FloorPlanResponse) => {
    const currentElements = stateRef.current.elements;
    const newLocalTables = currentElements.filter(el => el.type === "table" && !isObjectId(el.id));
    if (newLocalTables.length === 0 || !response?.tables) return;

    const existingIds = new Set(currentElements.filter(el => isObjectId(el.id)).map(el => el.id));
    const newBackendTables = response.tables.filter(t => !existingIds.has(t._id));
    if (newBackendTables.length === 0) return;

    const idMap = new Map<string, string>();
    for (const localEl of newLocalTables) {
      const match = newBackendTables.find(bt =>
        bt.name === localEl.props.name &&
        bt.sectionId === localEl.sectionId &&
        !Array.from(idMap.values()).includes(bt._id)
      );
      if (match) idMap.set(localEl.id, match._id);
    }
    if (idMap.size > 0) {
      const updatedElements = currentElements.map(el =>
        idMap.has(el.id) ? { ...el, id: idMap.get(el.id)! } : el
      );
      rawDispatch({ t: "LOAD", s: { ...stateRef.current, elements: updatedElements } });
    }
  }, []);

  const scheduleAutoSave = useCallback(() => {
    if (!restaurantId) {
      setSS("unsaved");
      return;
    }

    setSS("unsaved");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setSS("saving");
        const payload = toFloorPlanPayload(stateRef.current);
        const response = await floorPlanApi.patchFloorPlan(
          restaurantId,
          payload,
          { token: accessTokenRef.current }
        );
        syncNewTableIds(response);
        setSS("saved");
      } catch (error) {
        setSS("unsaved");
        console.error("Auto-save failed:", error);
        showToastRef.current("Autosave failed. Use Save Layout.", false);
      }
    }, 3200);
  }, [restaurantId, syncNewTableIds]); // accessToken removed — use ref instead

  // Dispatch wrapper: triggers debounced auto-save for every state mutation
  const dispatch = useCallback((action: Act) => {
    rawDispatch(action);
    if (action.t !== "LOAD") {
      scheduleAutoSave();
    }
  }, [scheduleAutoSave]);

  // Drag interaction tracking
  const iRef = useRef<{
    kind:"move"|"resize"|"rotate"|"pan"|"sel";
    eid?:string; hdir?:HDir;
    sm:Pos; sp?:Pos; ss?:{w:number;h:number}; sr?:number; ec?:Pos;
  }|null>(null);
  const [panning,setPanning]=useState(false);

  // Sidebar DnD
  const sdRef = useRef<{type:"table"|"struct";preset?:typeof TPRESETS[0];struct?:typeof STRUCTS[0]}|null>(null);

  const selectedEl = state.elements.find(e=>e.id===selId)||null;

  const toWorld = useCallback((cx:number,cy:number):Pos=>{
    const r=canvasRef.current?.getBoundingClientRect();
    if(!r) return{x:0,y:0};
    return{x:(cx-r.left-pan.x)/zoom, y:(cy-r.top-pan.y)/zoom};
  },[pan,zoom]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!restaurantId) {
        setSS("unsaved");
        showToast("Missing restaurant id. Persistence disabled.", false);
        return;
      }
      
      try {
        setInitialLoad(true);
        const response = await floorPlanApi.getFloorPlan(restaurantId, { token: accessTokenRef.current });
        if (cancelled) return;
        const next = normalizeFloorPlan(response);
        rawDispatch({ t: "LOAD", s: next });
        setHistStates([next]);
        setHIdx(0);
        setSelId(null);
        setSelIds([]);
        setSS("saved");
      } catch {
        if (cancelled) return;
        setSS("unsaved");
        showToastRef.current("Failed to load layout. You can still edit locally.", false);
      } finally {
        setInitialLoad(false);
      };
    };

    load();

    return () => {
      cancelled = true;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [restaurantId]); // accessToken intentionally excluded — use accessTokenRef to avoid reload on token refresh

  // ── KEYBOARD ────────────────────────────────────────────────────────────
  useEffect(()=>{
    const h=(e:KeyboardEvent)=>{
      const tag=(e.target as HTMLElement).tagName;
      if(tag==="INPUT"||tag==="TEXTAREA"||tag==="SELECT") return;

      if((e.ctrlKey||e.metaKey)&&e.key==="z"&&!e.shiftKey){
        const ni=Math.max(0,hIdx-1);
        rawDispatch({t:"LOAD",s:histStates[ni]}); setHIdx(ni);
        scheduleAutoSave();
        return;
      }
      if((e.ctrlKey||e.metaKey)&&(e.key==="y"||(e.shiftKey&&e.key==="z"))){
        const ni=Math.min(histStates.length-1,hIdx+1);
        rawDispatch({t:"LOAD",s:histStates[ni]}); setHIdx(ni);
        scheduleAutoSave();
        return;
      }
      if((e.key==="Delete"||e.key==="Backspace")&&selId){
        if(selIds.length>1){dispatch({t:"DELM",ids:selIds});setSelIds([]);}
        else dispatch({t:"DEL",id:selId});
        setSelId(null);
        return;
      }
      if((e.ctrlKey||e.metaKey)&&e.key==="d"&&selId){
        e.preventDefault();
        dispatch({t:"DUP",id:selId});
        showToast("Duplicated","info" as any);
      }
      if(e.key==="Escape"){setSelId(null);setSelIds([]);}
      if(e.key==="g"||e.key==="G") setGrid(v=>!v);
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[selId,selIds,hIdx,histStates,dispatch,scheduleAutoSave]);

  // ── CANVAS INTERACTIONS ──────────────────────────────────────────────────
  const onCanvasMD = useCallback((e:React.MouseEvent)=>{
    if(e.button===1||(e.button===0&&e.altKey)){
      iRef.current={kind:"pan",sm:{x:e.clientX,y:e.clientY}};
      setPanning(true); return;
    }
    if(e.button===0&&e.target===e.currentTarget){
      setSelId(null);setSelIds([]);
      const wp=toWorld(e.clientX,e.clientY);
      iRef.current={kind:"sel",sm:wp};
      setSelBox({s:wp,e:wp});
    }
  },[toWorld]);

  const onElemMD = useCallback((e:React.MouseEvent,id:string)=>{
    e.stopPropagation();
    if(canvasLock) return;
    const el=stateRef.current.elements.find(x=>x.id===id);
    if(!el||el.locked) return;
    if(e.shiftKey){
      setSelIds(ids=>ids.includes(id)?ids.filter(x=>x!==id):[...ids,id]);
      return;
    }
    setSelId(id); setSelIds([]);
    iRef.current={kind:"move",eid:id,sm:{x:e.clientX,y:e.clientY},sp:{...el.position}};
  },[canvasLock]);

  const onResizeMD = useCallback((hdir:HDir,e:React.MouseEvent,id:string)=>{
    const el=stateRef.current.elements.find(x=>x.id===id); if(!el) return;
    iRef.current={kind:"resize",eid:id,hdir,sm:{x:e.clientX,y:e.clientY},sp:{...el.position},ss:{w:el.size.width,h:el.size.height}};
  },[]);

  const onRotateMD = useCallback((e:React.MouseEvent,id:string)=>{
    const el=stateRef.current.elements.find(x=>x.id===id); if(!el) return;
    iRef.current={kind:"rotate",eid:id,sm:{x:e.clientX,y:e.clientY},sr:el.rotation,
      ec:{x:el.position.x+el.size.width/2, y:el.position.y+el.size.height/2}};
  },[]);

  const onMM = useCallback((e:React.MouseEvent)=>{
    const d=iRef.current; if(!d) return;
    const dx=(e.clientX-d.sm.x)/zoom, dy=(e.clientY-d.sm.y)/zoom;

    if(d.kind==="pan"){
      const pdx=e.clientX-d.sm.x, pdy=e.clientY-d.sm.y;
      setPan(p=>({x:p.x+pdx,y:p.y+pdy}));
      iRef.current={...d,sm:{x:e.clientX,y:e.clientY}};
      return;
    }
    if(d.kind==="sel"){
      const wp=toWorld(e.clientX,e.clientY);
      setSelBox(b=>b?{s:b.s,e:wp}:null);
      return;
    }
    if(d.kind==="move"&&d.eid&&d.sp){
      let nx=d.sp.x+dx, ny=d.sp.y+dy;
      if(snapOn){nx=snap(nx);ny=snap(ny);}
      rawDispatch({t:"UPD",id:d.eid,up:{position:{x:Math.max(0,nx),y:Math.max(0,ny)}}});
      return;
    }
    if(d.kind==="resize"&&d.eid&&d.sp&&d.ss&&d.hdir){
      const h=d.hdir;
      let nx=d.sp.x,ny=d.sp.y,nw=d.ss.w,nh=d.ss.h;
      if(h.includes("e")) nw=Math.max(40,d.ss.w+dx);
      if(h.includes("s")) nh=Math.max(20,d.ss.h+dy);
      if(h.includes("w")){nx=d.sp.x+dx; nw=Math.max(40,d.ss.w-dx);}
      if(h.includes("n")){ny=d.sp.y+dy; nh=Math.max(20,d.ss.h-dy);}
      if(snapOn){nw=snap(nw);nh=snap(nh);}
      rawDispatch({t:"UPD",id:d.eid,up:{position:{x:nx,y:ny},size:{width:nw,height:nh}}});
      return;
    }
    if(d.kind==="rotate"&&d.eid&&d.ec!==undefined){
      const wp=toWorld(e.clientX,e.clientY);
      let ang=(Math.atan2(wp.y-d.ec!.y, wp.x-d.ec!.x)*180/Math.PI+90)%360;
      if(ang<0) ang+=360;
      rawDispatch({t:"UPD",id:d.eid,up:{rotation:Math.round(ang)}});
    }
  },[zoom,snapOn,toWorld]);

  const onMU = useCallback((e:React.MouseEvent)=>{
    const interactionKind = iRef.current?.kind;

    if(iRef.current?.kind==="sel"&&selBox){
      const minX=Math.min(selBox.s.x,selBox.e.x), maxX=Math.max(selBox.s.x,selBox.e.x);
      const minY=Math.min(selBox.s.y,selBox.e.y), maxY=Math.max(selBox.s.y,selBox.e.y);
      if(maxX-minX>4||maxY-minY>4){
        const inBox=stateRef.current.elements.filter(el=>
          el.position.x>=minX&&el.position.x+el.size.width<=maxX&&
          el.position.y>=minY&&el.position.y+el.size.height<=maxY
        );
        if(inBox.length){setSelIds(inBox.map(e=>e.id));setSelId(inBox[0].id);}
      }
      setSelBox(null);
    }
    if(interactionKind==="move"||interactionKind==="resize"||interactionKind==="rotate"){
      scheduleAutoSave();
    }
    iRef.current=null;
    setPanning(false);
  },[selBox, scheduleAutoSave]);

  const onWheel = useCallback((e:React.WheelEvent)=>{
    e.preventDefault();
    const f=e.deltaY<0?1.1:0.9;
    const r=canvasRef.current!.getBoundingClientRect();
    const ox=e.clientX-r.left, oy=e.clientY-r.top;
    setZoom(z=>{const nz=clamp(z*f,.2,4); setPan(p=>({x:ox-(ox-p.x)*(nz/z),y:oy-(oy-p.y)*(nz/z)})); return nz;});
  },[]);

  // ── CANVAS DROP ──────────────────────────────────────────────────────────
  const onDrop = useCallback((e:React.DragEvent)=>{
    e.preventDefault();
    const d=sdRef.current; if(!d) return;
    const wp=toWorld(e.clientX,e.clientY);
    const px=snapOn?snap(wp.x):wp.x, py=snapOn?snap(wp.y):wp.y;

    if(d.type==="struct"&&d.struct){
      const el:FloorElement={
        id:uid(),type:d.struct.type,sectionId:state.sections[0]?.id||"",
        position:{x:Math.max(0,px-d.struct.dw/2),y:Math.max(0,py-d.struct.dh/2)},
        size:{width:d.struct.dw,height:d.struct.dh},rotation:0,
        zIndex:state.elements.length,props:{name:d.struct.lbl},
      };
      dispatch({t:"ADD",el});
      setSelId(el.id);
      pushHist({...state,elements:[...state.elements,el]});
    } else if(d.type==="table"&&d.preset){
      const pos={x:Math.max(0,px-d.preset.dw/2),y:Math.max(0,py-d.preset.dh/2)};
      setTM({mode:"create",preset:d.preset,dropPos:pos});
    }
    sdRef.current=null;
  },[toWorld,snapOn,state,pushHist,dispatch]);

  // ── SAVE & EXPORT ────────────────────────────────────────────────────────
  const handleSave = async()=>{
    if (!restaurantId) {
      setSS("unsaved");
      showToast("Missing restaurant id. Cannot save.", false);
      return;
    }

    setSS("saving");
    try {
      if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
      const response = await floorPlanApi.putFloorPlan(restaurantId, toFloorPlanPayload(state), { token: accessTokenRef.current });
      syncNewTableIds(response);
      setSS("saved");
      showToast("Layout saved!");
      pushHist(stateRef.current);
    } catch {
      setSS("unsaved");
      showToast("Save failed. Please retry.", false);
    }
  };

  const handleExport = ()=>{
    const blob=new Blob([JSON.stringify(state,null,2)],{type:"application/json"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="floor-plan.json"; a.click();
    showToast("Exported as JSON");
  };

  const visible = activeSec ? state.elements.filter(e=>e.sectionId===activeSec) : state.elements;

  // ─── TOOLBAR BUTTON ────────────────────────────────────────────────────
  const TB: FC<{onClick?:()=>void; title?:string; active?:boolean; disabled?:boolean;children:ReactNode}> = ({onClick,title,active,disabled,children}) => (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{display:"flex",alignItems:"center",gap:5,padding:"6px 10px",borderRadius:8,border:`1px solid ${active?"#fed7aa":"#e2e8f0"}`,background:active?"#fff7ed":"#fff",
        color:active?"#c2410c":"#374151",cursor:disabled?"not-allowed":"pointer",fontSize:11,fontWeight:700,opacity:disabled?.45:1,transition:"all .15s",whiteSpace:"nowrap"}}>
      {children}
    </button>
  );

  return (
    initialLoad ? 
        <div className="p-8 bg-slate-100 min-h-full flex flex-col gap-3 items-center justify-center">
          <Spinner />
          <p>Loading restaurant floor...</p>
        </div>
     :
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Outfit:wght@400;500;600;700;800&display=swap');
        *{font-family:'Outfit',sans-serif;box-sizing:border-box;}body{margin:0;overflow:hidden;}
        ::-webkit-scrollbar{width:4px;height:4px;}::-webkit-scrollbar-track{background:transparent;}::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:3px;}
        input[type=range]{accent-color:#f97316;}
        @keyframes fadeUp{from{opacity:0;transform:translateX(-50%) translateY(8px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
      `}</style>

      <div style={{display:"flex",flexDirection:"column",height:"90vh",background:"#f8fafc",overflow:"hidden"}}>

        {/* ═══ TOOLBAR ══════════════════════════════════════════════════ */}
        <div style={{height:50,background:"#FFF8E7",borderBottom:"1px solid #fde68a",display:"flex",alignItems:"center",gap:6,padding:"0 14px",flexShrink:0,zIndex:21,boxShadow:"0 1px 6px rgba(0,0,0,.07)"}}>

          <TB onClick={()=>setSBO(!sbarOpen)} active={sbarOpen} title="Toggle Sidebar">
            <I p={IC.menu} s={15}/>
          </TB>

          <div style={{width:1,height:20,background:"#e2e8f0",margin:"0 2px"}}/>

          <TB onClick={()=>setGrid(!grid)} active={grid} title="Toggle Grid (G)">
            <I p={IC.grid} s={14}/>Grid
          </TB>
          <TB onClick={()=>setSnap(!snapOn)} active={snapOn} title="Snap to Grid">
            <I p="M12 2v20M2 12h20M7 7l10 10M17 7L7 17" s={14}/>Snap
          </TB>
          <TB onClick={()=>setCL(!canvasLock)} active={canvasLock} title="Lock Layout">
            <I p={canvasLock?IC.lock:IC.unlock} s={14}/>{canvasLock?"Locked":"Lock"}
          </TB>

          <div style={{width:1,height:20,background:"#e2e8f0",margin:"0 2px"}}/>

          <TB onClick={()=>{const ni=Math.max(0,hIdx-1);rawDispatch({t:"LOAD",s:histStates[ni]});setHIdx(ni);scheduleAutoSave();}} disabled={hIdx<=0} title="Undo (Ctrl+Z)">
            <I p={IC.undo} s={14}/>Undo
          </TB>

          <TB onClick={()=>{const ni=Math.min(histStates.length-1,hIdx+1);rawDispatch({t:"LOAD",s:histStates[ni]});setHIdx(ni);scheduleAutoSave();}} disabled={hIdx>=histStates.length-1} title="Redo (Ctrl+Y)">
            <I p={IC.redo} s={14}/>Redo
          </TB>

          <div style={{width:1,height:20,background:"#e2e8f0",margin:"0 2px"}}/>

          {/* Zoom */}
          <div style={{display:"flex",alignItems:"center",gap:2,background:"#f1f5f9",borderRadius:8,padding:"4px 8px",border:"1px solid #e2e8f0"}}>
            <button onClick={()=>setZoom(z=>clamp(z*.8,.2,4))} style={{background:"none",border:"none",cursor:"pointer",padding:"1px 3px",display:"flex",color:"#64748b"}}><I p={IC.zminus} s={14}/></button>
            <span style={{fontSize:11,fontWeight:800,color:"#374151",minWidth:38,textAlign:"center"}}>{Math.round(zoom*100)}%</span>
            <button onClick={()=>setZoom(z=>clamp(z*1.25,.2,4))} style={{background:"none",border:"none",cursor:"pointer",padding:"1px 3px",display:"flex",color:"#64748b"}}><I p={IC.zplus} s={14}/></button>
          </div>
          <button onClick={()=>{setZoom(0.5);setPan({x:40,y:40});}} style={{background:"#f1f5f9",border:"1px solid #e2e8f0",cursor:"pointer",borderRadius:8,padding:"5px 9px",fontSize:11,fontWeight:700,color:"#64748b"}}>
            Reset
          </button>

          <div style={{flex:1}}/>

          {/* Element count */}
          <span style={{fontSize:11,color:"#94a3b8",fontWeight:600}}>{state.elements.length} elements</span>

          <div style={{width:1,height:20,background:"#e2e8f0",margin:"0 2px"}}/>

          <TB onClick={handleExport} title="Export JSON">
            <I p={IC.down} s={14}/>Export
          </TB>

          {!persistenceEnabled && (
            <span style={{fontSize:11,fontWeight:700,color:"#b91c1c",background:"#fef2f2",border:"1px solid #fecaca",padding:"5px 9px",borderRadius:8}}>
              Missing restaurant id - persistence disabled
            </span>
          )}

          {/* Save button */}
          <button onClick={handleSave} disabled={!persistenceEnabled} style={{
            display:"flex",alignItems:"center",gap:6,padding:"8px 16px",
            background:saveStatus==="saved"?"#0f172a":"#f97316",color:"#fff",
            border:"none",borderRadius:10,fontSize:12,fontWeight:800,
            transition:"all .2s",boxShadow:saveStatus==="unsaved"?"0 4px 14px rgba(249,115,22,.4)":"none",
            opacity:persistenceEnabled?1:.6,
            cursor:persistenceEnabled?"pointer":"not-allowed",
          }}>
            {saveStatus==="saving"
              ? <span style={{animation:"pulse 1s infinite"}}>Saving…</span>
              : <><I p={IC.save} s={14}/>{persistenceEnabled ? (saveStatus==="saved"?"Saved ✓":"Save Layout") : "Save Disabled"}</>
            }
          </button>
        </div>

        {/* ═══ BODY ════════════════════════════════════════════════════ */}
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>
          {/* ── SIDEBAR ─────────────────────────────────────────────── */}
          {sbarOpen && (
            <Sidebar
              restaurantId={restaurantId}
              state={state}
              dispatch={dispatch}
              activeSec={activeSec}
              setActiveSec={setAS}
              onTableClick={p=>setTM({mode:"create",preset:p})}
              onTableDragStart={p=>{sdRef.current={type:"table",preset:p};}}
              onStructDragStart={s=>{sdRef.current={type:"struct",struct:s};}}
            />
          )}

          {/* ── CANVAS ──────────────────────────────────────────────── */}
          <div
            ref={canvasRef}
            style={{
              flex:1,position:"relative",overflow:"hidden",
              cursor:panning?"grabbing":iRef.current?.kind==="rotate"?"crosshair":"default",
              background:grid
                ? "radial-gradient(circle, #c8d3de 1px, transparent 1px)"
                : "#f1f5f9",
              backgroundSize:grid?`${GS*zoom}px ${GS*zoom}px`:undefined,
              backgroundPosition:grid?`${pan.x}px ${pan.y}px`:undefined,
            }}
            onMouseDown={onCanvasMD}
            onMouseMove={onMM}
            onMouseUp={onMU}
            onMouseLeave={()=>{iRef.current=null;setPanning(false);setSelBox(null);}}
            onWheel={onWheel}
            onDragOver={e=>e.preventDefault()}
            onDrop={onDrop}
          >
            {/* World transform */}
            <div style={{position:"absolute",left:pan.x,top:pan.y,transform:`scale(${zoom})`,transformOrigin:"0 0"}}>

              {[...visible].sort((a,b)=>a.zIndex-b.zIndex).map(el=>{
                const isSel=el.id===selId||selIds.includes(el.id);
                const isHov=el.id===hovId;
                return (
                  <div key={el.id}
                    onMouseEnter={()=>setHovId(el.id)}
                    onMouseLeave={()=>setHovId(null)}>
                    {el.type==="table"
                      ? <TableEl  el={el} sel={isSel} hov={isHov} onMD={e=>onElemMD(e,el.id)}/>
                      : <StructEl el={el} sel={isSel} hov={isHov} onMD={e=>onElemMD(e,el.id)}/>
                    }
                    {isSel&&el.id===selId&&!el.locked&&!canvasLock&&(
                      <Handles el={el}
                        onResize={(h,e)=>onResizeMD(h,e,el.id)}
                        onRotate={e=>onRotateMD(e,el.id)}/>
                    )}
                  </div>
                );
              })}

              {/* Selection box */}
              {selBox&&(
                <div style={{
                  position:"absolute",
                  left:Math.min(selBox.s.x,selBox.e.x),top:Math.min(selBox.s.y,selBox.e.y),
                  width:Math.abs(selBox.e.x-selBox.s.x),height:Math.abs(selBox.e.y-selBox.s.y),
                  border:"2px dashed #f97316",background:"rgba(249,115,22,.06)",
                  borderRadius:4,pointerEvents:"none",
                }}/>
              )}
            </div>

            {/* ── CANVAS OVERLAYS ──────────────────────────────────── */}

            {/* Bottom left: info bar */}
            <div style={{position:"absolute",bottom:12,left:12,display:"flex",gap:8,pointerEvents:"none",flexWrap:"wrap"}}>
              <div style={{background:"rgba(15,23,42,.72)",color:"#e2e8f0",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:700,backdropFilter:"blur(6px)",whiteSpace:"nowrap"}}>
                {state.elements.length} elements · {state.sections.length} sections · {Math.round(zoom*100)}%
              </div>
              {activeSec&&(
                <div style={{background:state.sections.find(s=>s.id===activeSec)?.color||"#f97316",color:"#fff",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:800,boxShadow:"0 2px 8px rgba(0,0,0,.15)"}}>
                  Viewing: {state.sections.find(s=>s.id===activeSec)?.name}
                  <button onClick={()=>setAS(null)} style={{marginLeft:7,background:"rgba(255,255,255,.3)",border:"none",cursor:"pointer",borderRadius:4,padding:"0 4px",color:"#fff",fontSize:10,fontWeight:800}}>×</button>
                </div>
              )}
              {selIds.length>1&&(
                <div style={{background:"#0f172a",color:"#fff",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:800}}>
                  {selIds.length} selected
                  <button onClick={()=>{ rawDispatch({t:"DELM",ids:selIds});setSelIds([]);setSelId(null); }}
                    style={{marginLeft:8,background:"rgba(239,68,68,.8)",border:"none",cursor:"pointer",borderRadius:4,padding:"1px 6px",color:"#fff",fontSize:10}}>
                    Delete all
                  </button>
                </div>
              )}
            </div>

            {/* Bottom right: status legend */}
            <div style={{position:"absolute",bottom:12,right:12,display:"flex",gap:5,flexWrap:"wrap",justifyContent:"flex-end",pointerEvents:"none"}}>
              {(Object.keys(ST) as TStatus[]).map(st=>{
                const c=ST[st];
                return (
                  <div key={st} style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,.9)",borderRadius:8,padding:"4px 9px",backdropFilter:"blur(4px)",border:`1px solid ${c.bd}30`,boxShadow:"0 1px 4px rgba(0,0,0,.07)"}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:c.dot}}/>
                    <span style={{fontSize:10,fontWeight:700,color:c.tx}}>{c.lbl}</span>
                  </div>
                );
              })}
            </div>

            {/* Keyboard shortcuts hint */}
            <div style={{position:"absolute",top:12,right:12,pointerEvents:"none"}}>
              <div style={{background:"rgba(255,255,255,.85)",backdropFilter:"blur(6px)",borderRadius:10,padding:"7px 12px",border:"1px solid #e2e8f0",fontSize:10,color:"#94a3b8",lineHeight:1.8}}>
                <b style={{color:"#374151"}}>Shortcuts:</b><br/>
                Del / Backspace → delete<br/>
                Ctrl+D → duplicate<br/>
                Ctrl+Z/Y → undo/redo<br/>
                Alt+drag → pan<br/>
                Scroll → zoom<br/>
                G → toggle grid
              </div>
            </div>

            {/* Empty state */}
            {visible.length===0&&(
              <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,pointerEvents:"none"}}>
                <div style={{width:72,height:72,borderRadius:"50%",background:"#f1f5f9",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <I p="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18" s={32}/>
                </div>
                <div style={{textAlign:"center"}}>
                  <p style={{margin:0,fontSize:16,fontWeight:800,color:"#94a3b8"}}>Your canvas is empty</p>
                  <p style={{margin:"4px 0 0",fontSize:12,color:"#cbd5e1"}}>Drag tables or structural items from the sidebar, or click a table preset</p>
                </div>
              </div>
            )}
          </div>

          {/* ── PROPERTIES PANEL ───────────────────────────────────── */}
          <PropsPanel
            el={selectedEl}
            sections={state.sections}
            dispatch={dispatch}
            onEditTable={el=>setTM({mode:"edit",el})}
            onClose={()=>setSelId(null)}
          />
        </div>
      </div>

      {/* ═══ TABLE MODAL ══════════════════════════════════════════════ */}
      {tModal&&(
        <TableModal
          mode={tModal.mode}
          init={tModal.mode==="edit"&&tModal.el ? {
            name:   tModal.el.props.name||"",
            seats:  tModal.el.props.seats||4,
            shape:  tModal.el.props.shape||"square",
            sectionId: tModal.el.sectionId,
            status: tModal.el.props.status||"available",
            notes:  tModal.el.props.notes||"",
            active: tModal.el.props.active??true,
          } : tModal.preset ? {
            seats:  tModal.preset.seats||4,
            shape:  tModal.preset.shape,
            sectionId: state.sections[0]?.id||"",
          } : undefined}
          sections={state.sections}
          onClose={()=>setTM(null)}
          onSave={form=>{
            if(tModal.mode==="create"){
              const p=tModal.preset;
              const pos=tModal.dropPos||{x:80+Math.random()*200,y:80+Math.random()*160};
              const el:FloorElement={
                id:uid(),type:"table",sectionId:form.sectionId,
                position:pos,
                size:{width:p?.dw||(form.shape==="rectangle"?124:84),height:p?.dh||(form.shape==="rectangle"?72:84)},
                rotation:0,zIndex:state.elements.length,
                props:{seats:form.seats,shape:form.shape,name:form.name,status:form.status,notes:form.notes,active:form.active},
              };
              dispatch({t:"ADD",el});
              setSelId(el.id);
              const ns={...state,elements:[...state.elements,el]};
              setHistStates(h=>[...h.slice(0,hIdx+1),ns]);
              setHIdx(i=>i+1);
              showToast(`Table ${form.name} added to canvas!`);
            } else if(tModal.mode==="edit"&&tModal.el){
              dispatch({t:"UPD",id:tModal.el.id,up:{sectionId:form.sectionId,props:{...tModal.el.props,...form}}});
              showToast("Table updated");
            }
            setTM(null);
          }}
        />
      )}

      {/* ═══ TOAST ════════════════════════════════════════════════════ */}
      {toast&&(
        <div style={{
          position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:9999,
          background:"#0f172a",color:"#fff",borderRadius:14,
          padding:"11px 22px",fontSize:13,fontWeight:700,
          boxShadow:"0 10px 30px rgba(0,0,0,.3)",
          display:"flex",alignItems:"center",gap:9,
          animation:"fadeUp .25s ease",whiteSpace:"nowrap",
        }}>
          <I p={IC.check} s={15}/>
          {toast.msg}
        </div>
      )}
    </>
  );
}
